<template>
    <div class="card card-default work-order-card" v-on="isCollapsed ? { click: expand } : {}" :class="{ 'work-order-expanded': isExpanded, 'work-order-collapsed' : !isExpanded }">
        <div class="card-header">
            <div class="d-inline-block card-header-content">
                <div class="title">
                    <span v-if="isExpanded && !standalone" class="h6 clickable minus-button" @click="collapse">[–]</span>
                    {{ workorder.title }}
                </div>
                <div class="date">

                    <span v-if="showEstimatedStartDate" class="date">
                        Start date is in {{ daysUntilStartString }} ({{ startDate }})
                    </span>
                    <span v-else v-tooltip.bottom="hoverDate(workorder.updated_at)" :title="hoverDate(workorder.updated_at)">
                        Last updated {{ prettyDate(workorder.updated_at) }}
                    </span>

                    <div v-if="showNotificationOption" class="d-inline-block text-small">
                         • Send team notifications via <span :class="pendingNotificationOption ? '' : 'link'" @click="updateNotificationClicked">{{  notificationOption }}</span>
                         <span v-if="!workorder.use_project_notification_settings" class="clickable remove-slack-button" @click="resetNotifications">Reset</span>
                    </div>
                </div>
                <div v-if="isExpanded && !standalone" class="mt-1">
                    <div class="link d-inline-block" @click="toggleEdit" >{{ currentlyEditing ? 'Cancel' : 'Edit' }} </div>
                    <div v-if="isDeleting" class="d-inline-block ml-2">
                        <div><span class="loader loader-xs mr-2"></span>Deleting...</div>
                    </div>
                    <div v-else-if="!showingDeleteConfirmation" class="link d-inline-block ml-2" @click="showDeleteConfirmation">Delete</div>
                    <div v-else class="d-inline-block ml-2">
                        <span class="font-weight-600">Are you sure?</span>
                        <span class="clickable text-danger ml-2" @click="deleteWorkOrder">Yes</span>
                        <span class="link ml-2" @click="showingDeleteConfirmation = false">Cancel</span>
                    </div>
                    <div v-if="deleteStatusText" class='text-danger'>
                        {{ deleteStatusText }}
                    </div>
                </div>
                <emails v-if="hasEmails" :emails="emails" :isExpanded="isExpanded"></emails>
            </div>
            <div v-if="isExpanded" class="btn-group float-right">
              <button type="button" class="btn dropdown-toggle" :class="statusButtonClass" :disabled="savingStatus" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                <span v-if="savingStatus" class="loader loader-sm float-left mr-2"></span>
                <span v-else>
                    <i :class="statusButtonIcon"></i>
                </span>
                <span class="ml-2">{{ status }}</span>
              </button>

              <div class="dropdown-menu dropdown-menu-right">
                <div class="dropdown-item" :class="status == 'Not started' ? '' : 'clickable'" @click="setStatus('Not started')"><i class="far fa-stop-circle"></i> <span class="ml-2">Not started</span></div>
                <div class="dropdown-item" :class="status == 'In progress' ? '' : 'clickable'" @click="setStatus('In progress')"><i class="fas fa-tasks"></i> <span class="ml-2">In progress</span></div>
                <div class="dropdown-item" :class="status == 'Complete' ? '' : 'clickable'" @click="setStatus('Complete')"><i class="far fa-check-circle"></i> <span class="ml-2">Complete</span></div>
              </div>

                <div v-show="workorder.notify_team && !this.currentlyEditing" @click="enableOrDisableNotifications" class="clickable notification-icon" :class="savingNotificationSettings ? 'disabled' : ''" v-tooltip.bottom="'Team notifications for this work order are ON'">
                    <i class="fas fa-bell mt-1 ml-3"></i>
                </div>
                <div v-show="!workorder.notify_team && !this.currentlyEditing" @click="enableOrDisableNotifications" class="clickable notification-icon do-not-notify" :class="savingNotificationSettings ? 'disabled' : ''" v-tooltip.bottom="'Team notifications for this work order are OFF'">
                    <i class="fas fa-bell-slash mt-1 ml-3"></i>
                </div>

            </div>
            <span v-else class="work-order-alert float-right">
                <i :class="statusButtonIcon"></i><span class="ml-2">{{ workorder.status }}</span>
            </span>
            <div v-if="error" class="text-danger text-md-right">Updating the work order status failed. Please try again or get in touch!</div>
        </div>
        <div v-if="isExpanded" class="card-body">
            <work-order-form v-if="currentlyEditing" v-bind:is-editing="true" :project="project" :work-order="workorder"></work-order-form>
            <div v-else>
                <div class="row mb-2">
                    <div class="col-md-3 text-muted">
                        Start date
                    </div>
                    <div class="col-md-9">
                        <span v-if="startDate">{{ startDate }}</span>
                        <span v-else class="text-disabled">No start date</span>
                    </div>
                </div>

                <div class="row mb-2">
                    <div class="col-md-3 text-muted">
                        End date
                    </div>
                    <div class="col-md-9">
                        <span v-if="endDate">{{ endDate }}</span>
                        <span v-else class="text-disabled">No end date</span>
                    </div>
                </div>

                <div class="row mb-2">
                    <div class="col-md-3 text-muted">
                        Description
                    </div>
                    <div class="col-md-9">
                        <span v-if="description" class="multi-line">{{ description }}</span>
                        <span v-else class="text-disabled">No description</span>
                    </div>
                </div>

                <div class="row mb-2">
                    <div class="col-md-3 text-muted">
                        Material location
                    </div>
                    <div class="col-md-9">
                        <span v-if="workorder.material_location" class="multi-line">{{ workorder.material_location }}</span>
                        <span v-else class="text-disabled">No material location provided</span>

                    </div>
                </div>

                <div class="row mb-2">
                    <div class="col-md-3 text-muted">
                        Assigned team members
                    </div>
                    <div class="col-md-9">
                        <div v-if="workorder.crew && workorder.crew.length > 0">
                            <div v-for="crewMember in workorder.crew">
                                <span>{{crewMember.name}}</span>
                                <br />
                            </div>
                        </div>
                        <span v-else class="text-disabled">No team members assigned</span>
                    </div>
                </div>

                <div class="row mb-2">
                    <div class="col-md-3 text-muted">
                        Depends on
                    </div>
                    <div class="col-md-9">
                        <div v-if="workorder.all_required_work_orders && workorder.all_required_work_orders.length > 0">
                            <div v-for="requiredWorkOrders in workorder.all_required_work_orders" :key="requiredWorkOrders.id">
                                <span>{{requiredWorkOrders.title}}</span>
                                <br />
                            </div>
                        </div>
                        <div v-else class="text-disabled">This work order is not blocked by anything</div>
                    </div>
                </div>

                <div class="row mb-2">
                    <div class="col-md-3 text-muted">
                        Email customer about status changes
                    </div>
                    <div class="col-md-1 my-auto">
                        <toggle-button :value="notifyCustomer"
                                       @change="notifyCustomerChanged"
                                       color="#000000"
                                       :sync="true"
                                       :labels="{checked: 'Yes', unchecked: 'No'}"
                                       />
                    </div>
                </div>
            </div>
        </div>
        <notes v-if="isExpanded" :type="'workorder'" :workorder="workorder" :user="user" :role="role" :key="workorder.id"></notes>
    </div>
</template>

<script>
    //import smoothReflow from 'vue-smooth-reflow';
    export default {
        props: ['role', 'project', 'workorder', 'standalone', 'user'],
        //mixins: [smoothReflow],
        created: function() {
            this.getBtnClassForStatus(this.workorder.status);
            var vm = this;
            Bus.$on('new-work-order-canceled', function() {
                vm.currentlyEditing = false;
            });
            Bus.$on('work-order-updated', function(workOrder) {
                if (workOrder.id == vm.workorder.id) {
                    vm.currentlyEditing = false;
                    if (vm.standalone) {
                        // should not directly set the workorder prop,
                        // find a cleaner way to handle this.
                        // https://stackoverflow.com/questions/39868963/vue-2-mutating-props-vue-warn
                        vm.workorder = workOrder;
                    }
                }
            });

            Bus.$on('email-tracking-event', function(emailTracking) {
                console.log("email-tracking-event BUS!");
                console.log(emailTracking);
                if (emailTracking.project_id == vm.workorder.project_id && emailTracking.work_order_id === vm.workorder.id) {
                    let index = _.findIndex(vm.emails, { guid: emailTracking.guid });
                    if (index > -1) {
                        Vue.set(vm.emails, index, emailTracking);
                    }
                }
            });

        },

        mounted: function() {
            // this.$smoothReflow({
            //     property: ['height'],
            //     transition: 'height .25s ease-in-out, width .75s ease-out'
            // });
        },

        data: function() {
            return {
                currentlyEditing: false,
                isExpanded: this.standalone,
                error: false,
                savingStatus: false,
                isDeleting: false,
                deleteStatusText: null,
                showingDeleteConfirmation: false,
                statusButtonClass: 'btn-light',
                statusAlertClass: 'alert-light work-order-alert',
                statusButtonIcon: 'fas fa-tasks',
                savingStatusText: 'Not started',
                notifyCustomer: this.workorder ? !!this.workorder.notify_customer : false,
                savingNote: false,
                showNoteInput: false,
                noteContent: '',
                uploadingImage: false,
                image: null,
                imagePath: '',
                localImageUrl: '',
                pendingNotificationOption: null,
                savingNotificationSettings: false,
                emails: this.workorder.emails
            };
        },

        watch: {
            workorder: function (newWorkOrder, oldWorkOrder) {
                if (newWorkOrder != oldWorkOrder) {
                    this.getBtnClassForStatus(newWorkOrder.status);
                    this.notifyCustomer = !!newWorkOrder.notify_customer;
                    this.emails = newWorkOrder.emails;
                }
            },

            notifyCustomer: function(newVal, oldVal) {
                this.notifyCustomer = newVal;
            },
        },

        computed: {
            hasEmails: function() {
                return this.workorder.emails && this.workorder.emails.length > 0;
            },

            daysSinceStart: function() {
                return moment().diff(this.workorder.start_date, 'days');
            },

            daysUntilStart: function() {
                return (this.daysSinceStart * -1) + 1;
            },

            daysUntilStartString: function() {
                let days = this.daysUntilStart;
                return String(days) + ' day' + (days == 1 ? '' : 's');
            },

            showEstimatedStartDate: function() {
                return this.workorder.status === 'Not started' && this.workorder.start_date;
            },

            notificationOption: function() {
                if (this.pendingNotificationOption) return this.pendingNotificationOption;
                return this.workorderNotificationOption;
            },

            workorderNotificationOption: function() {
                if (this.workorder.notify_slack && this.workorder.notify_email) {
                    return 'Slack & email';
                }
                if (this.workorder.notify_slack) {
                    return 'Slack';
                }
                if (this.workorder.notify_email) {
                    return 'email';
                }
            },

            showNotificationOption: function() {
                if (this.workorder.notify_team && !this.currentlyEditing && this.isExpanded && this.project.slack_webhook_channel) {
                    return true;
                }
                return false;
            },

            isCollapsed: function() {
                return !this.isExpanded;
            },

            description: function() {
                return this.workorder.description;
            },

            startDate: function() {
                if (this.workorder.start_date) {
                    return this.workOrderDate(this.workorder.start_date);
                }
                return null;
            },

            endDate: function() {
                if (this.workorder.end_date) {
                    return this.workOrderDate(this.workorder.end_date);
                }
                return null;
            },

            status: function() {
                if (this.savingStatus) {
                    return this.savingStatusText;
                }

                return this.workorder.status;
            },
        },

        methods: {
            updateNotificationClicked: function(event) {

                if (this.pendingNotificationOption) {
                    return;
                }

                if (this.notificationOption === 'Slack & email') {
                    this.pendingNotificationOption = 'Slack';
                }
                else if (this.notificationOption === 'Slack') {
                    this.pendingNotificationOption = 'email';
                }
                else if (this.notificationOption === 'email') {
                    this.pendingNotificationOption = 'Slack & email';
                }

                this.updateNotification();
            },

            updateNotification: function() {
                this.savingNotificationSettings = true;
                axios.post(`/api/workorders/${this.workorder.id}/notifications`, {
                            slack: this.pendingNotificationOption.toLowerCase().includes('slack'),
                            email: this.pendingNotificationOption.toLowerCase().includes('email')
                        })
                        .then(response => {
                            Bus.$emit('work-order-updated', response.data);
                            this.savingNotificationSettings = false;
                            this.pendingNotificationOption = null;
                        })
                        .catch(error => {
                            this.savingNotificationSettings = false;
                            this.pendingNotificationOption = null;
                        });
            },

            enableOrDisableNotifications: function() {
                this.savingNotificationSettings = true;
                axios.post(`/api/workorders/${this.workorder.id}/notifications`, {
                            enable: !this.workorder.notify_team
                        })
                        .then(response => {
                            this.savingNotificationSettings = false;
                            Bus.$emit('work-order-updated', response.data);
                        })
                        .catch(error => {
                            this.savingNotificationSettings = false;
                        });
            },

            resetNotifications: function() {
                this.savingNotificationSettings = true;
                axios.post(`/api/workorders/${this.workorder.id}/notifications`, {
                            reset: true
                        })
                        .then(response => {
                            this.savingNotificationSettings = false;
                            Bus.$emit('work-order-updated', response.data);
                        })
                        .catch(error => {
                            this.savingNotificationSettings = false;
                        });
            },

            notifyCustomerChanged: function(event) {
                this.notifyCustomer = event.value;
                this.updateNotifyCustomer();
            },

            showDeleteConfirmation: function() {
                this.showingDeleteConfirmation = true;
            },

            toggleEdit: function() {
                this.currentlyEditing = !this.currentlyEditing;
            },

            expand: function() {
                this.isExpanded = true;
            },

            collapse: function() {
                this.isExpanded = false;
            },

            hoverDate: function (date) {
                return moment.utc(date).local().format('MMMM D YYYY hh:mm A');
            },

            prettyDate: function(date)
            {
                return moment.utc(date).local().fromNow();
            },

            workOrderDate: function(date)
            {
                return moment(date).format('MMMM D YYYY');
            },

            getBtnClassForStatus: function(status) {
                if (status == 'Not started') {
                    //this.statusButtonClass = 'btn-light';
                    this.statusAlertClass = 'alert-light work-order-alert';
                    this.statusButtonIcon = 'far fa-stop-circle';
                }
                else if (status == 'In progress') {
                    //this.statusButtonClass = 'btn-success';
                    this.statusAlertClass = 'alert-success work-order-alert';
                    this.statusButtonIcon = 'fas fa-tasks';
                }
                else if (status == 'Complete') {
                    //this.statusButtonClass = 'btn-info';
                    this.statusAlertClass = 'alert-info work-order-alert';
                    this.statusButtonIcon = 'far fa-check-circle';
                }
                else {
                    this.statusButtonClass = 'btn-light';
                    this.statusAlertClass = 'alert-light work-order-alert';
                    this.statusButtonIcon = 'fas fa-tasks';
                }
            },

            setStatus: function(status) {
                this.error = false;

                if (this.workorder.status != status) {
                    this.getBtnClassForStatus(status);
                    this.savingStatusText = status;
                    this.savingStatus = true;
                    axios.post(`/api/workorders/${this.workorder.id}/status`, {
                            status: status
                        })
                        .then(response => {
                            this.savingStatus = false;
                            Bus.$emit('work-order-updated', response.data);
                            Bus.$emit('refresh-current-project'); //TODO: Just refresh the overview "progress bar" not the whole project
                            /*mixpanel.track('Work Order Status Changed', {
                                title: response.data.title,
                                status: status
                            });*/
                        })
                        .catch(error => {
                            this.getBtnClassForStatus(this.workorder.status);
                            this.savingStatus = false;
                            this.error = true;
                        });
                }
            },

            updateNotifyCustomer: function() {
                axios.post(`/api/workorders/${this.workorder.id}/notifyCustomer`, {
                    notifyCustomer: this.notifyCustomer
                })
                .then(response => {
                })
                .catch(error => {
                    this.notifyCustomer = this.workorder.notify_customer;
                });
            },

            deleteWorkOrder: function() {
                this.isDeleting = true;
                this.deleteStatusText = null;
                axios.delete(`/api/workorders/${this.workorder.id}`)
                    .then(response => {
                        this.isDeleting = false;
                        this.showingDeleteConfirmation = false;
                        Bus.$emit('work-order-deleted', response.data);
                    })
                    .catch(error => {
                        this.isDeleting = false;
                        this.showingDeleteConfirmation = false;
                        this.deleteStatusText = 'We couldn\'t delete this work order. Please try again or get in touch!';
                    });
            },
        }
    }
</script>
